// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import type { GroupedSongResult, SongMetadata } from "../../shared/src/song-results";
import {
  renderGroupedSongResultHtml,
  renderSongMetadata,
  type SongResultRenderContext as SharedSongResultRenderContext,
} from "../../shared/src/song-result-renderer";
export {
  renderSongMetadata,
  highlightInsensitive,
  variantLabelKey,
} from "../../shared/src/song-result-renderer";

/** Browser-only enhancements to the environment-independent presentation context. */
export type SongResultRenderContext = SharedSongResultRenderContext & {
  onDetailsRendered?: (body: HTMLElement) => void;
};

/** Initializes an existing SSR article without replacing content or adding duplicate handlers. */
export function initializeGroupedSongResult(
  article: HTMLElement,
  context: SongResultRenderContext,
  group?: GroupedSongResult,
): void {
  if (article.dataset.sbSongResultWired === "1") return;
  article.dataset.sbSongResultWired = "1";
  const metadata = new Map<string, Partial<SongMetadata>>();
  if (group) {
    metadata.set(`sb-song-${group.representative.ref}-shared`, group.commonMetadata);
    for (const appearance of group.appearances) {
      metadata.set(
        `sb-song-${group.representative.ref}-appearance-${appearance.original.ref}`,
        appearance.metadata,
      );
    }
  }
  for (const body of article.querySelectorAll<HTMLElement>(
    "[data-sb-search-native-details] [data-sb-search-details-body], [data-sb-song-common-details] [data-sb-search-details-body]",
  )) {
    context.onDetailsRendered?.(body);
  }
  for (const button of article.querySelectorAll<HTMLButtonElement>(
    "[data-sb-search-open-details]",
  )) {
    const id = button.getAttribute("aria-controls");
    const details = Array.from(
      article.querySelectorAll<HTMLElement>("[data-sb-search-details]"),
    ).find(element => element.id === id);
    const body = details?.querySelector<HTMLElement>("[data-sb-search-details-body]");
    if (!id || !details || !body) continue;
    button.addEventListener("click", () => {
      const open = button.getAttribute("aria-expanded") !== "true";
      if (open && details.dataset.sbLoaded !== "1") {
        const item = metadata.get(id);
        if (item)
          body.innerHTML = renderSongMetadata(item, {
            ...context,
            origin: context.origin ?? window.location.origin,
          });
        context.onDetailsRendered?.(body);
        details.dataset.sbLoaded = "1";
      }
      // Commit collapsed layout before the first lazy expansion starts.
      if (open) void details.offsetHeight;
      details.inert = !open;
      details.setAttribute("aria-hidden", String(!open));
      details.dataset.open = open ? "1" : "0";
      button.setAttribute("aria-expanded", String(open));
    });
  }
}

/** Creates a browser result with lazy disclosure bodies and existing animated behavior. */
export function renderGroupedSongResult(
  group: GroupedSongResult,
  context: SongResultRenderContext,
): HTMLElement {
  const template = document.createElement("template");
  template.innerHTML = renderGroupedSongResultHtml(group, {
    ...context,
    detailsMode: "lazy",
    origin: context.origin ?? window.location.origin,
  });
  const article = template.content.firstElementChild as HTMLElement;
  initializeGroupedSongResult(article, context, group);
  return article;
}
